import { useAppSelector } from './redux-hooks';

// How the order on screen is going to be settled.
//
// The venue decides what is on offer: a card now, only where Square is
// connected. The guest decides whether to take it — a connected seller
// still lets a guest walk up to the register.
//
// This mirrors the same rule the backend applies when it writes the
// order. The duplication is deliberate and narrow: here it decides what
// to draw, there it decides what to record, and the browser's copy is
// never trusted for the latter.

export type Fulfillment = 'square' | 'cash' | 'room';

/** What settles the order once a card is out of the picture. */
export type VenueSettlement = Exclude<Fulfillment, 'square'>;

export interface FulfillmentOptions {
    /** The venue takes cards, so the guest is asked which way to pay. */
    cardOffered: boolean;
    venue: VenueSettlement;
}

export function useFulfillmentOptions(): FulfillmentOptions {
    return useAppSelector((state) => {
        const menu = state.menu.menu;
        return {
            // Square is only reported as available once a location is
            // chosen too, so this alone is enough to know a card can be
            // charged.
            cardOffered: menu?.featurePayments === true,
            // Strictly 'hotel', never "not a restaurant": a typo or a
            // missing value in the CMS must not hand a guest the ability
            // to charge a room they may not be staying in.
            venue: menu?.establishmentType === 'hotel' ? 'room' : 'cash',
        };
    });
}

/** The settlement a venue falls back to when the guest is not paying by
 *  card — the answer for every order the server did not report one for. */
export function useFulfillment(): Fulfillment {
    return useFulfillmentOptions().venue;
}
